import type { ReactElement } from 'react';
import { Grid, useScreenSize } from '@stitch-fix/mode-react';
import { A, F } from '@mobily/ts-belt';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import { VisionStatusProvider } from '@stitch-fix/vision-shared';
import { ShopProductFragment } from '../../utils/hooks/getSearchResults';
import { useSearchParams } from '../../utils/hooks/useSearchParams';
import { insertElement } from '../../utils/helpers';
import { ResultsOutfits } from './ResultsOutfits';
import { PromoCard } from './PromoCard/PromoCard';
import ResultProductCard from './ResultProductCard';
const createProductElement = (product: ShopProductFragment, index: number, showFeedbackThumbs?: boolean, visionSeeItOnMe1ItemEnabled?: boolean, visionSeeItOnMeZoomEnabled?: boolean) => <ResultProductCard showFeedbackThumbs={showFeedbackThumbs} visionSeeItOnMe1ItemEnabled={visionSeeItOnMe1ItemEnabled} visionSeeItOnMeZoomEnabled={visionSeeItOnMeZoomEnabled} key={product.id} brand={product.brand.name} isVisionEligible={product.isVisionEligible} name={product.name} shopProductVariant={product?.variantBySkuId} index={index} />;
interface PartitionElementsForPageParams {
  shopProducts: ShopProductFragment[];
  page: number;
  splitIndex: number;
  showFeedbackThumbs?: boolean;
  visionSeeItOnMe1ItemEnabled?: boolean;
  visionSeeItOnMeZoomEnabled?: boolean;
}

/**
 * Handles splitting the products into the first and the rest of the rows, based on the splitIndex.
 * It inserts the promo card into the rest of the rows, when page is 1.
 */
const partitionElementsForPage = ({
  shopProducts,
  splitIndex,
  page,
  showFeedbackThumbs,
  visionSeeItOnMe1ItemEnabled,
  visionSeeItOnMeZoomEnabled
}: PartitionElementsForPageParams): [ReactElement[], ReactElement[]] => {
  const isPromoCardShown = page === 1;
  const productCards = shopProducts.map((product, index) => createProductElement(product, index, showFeedbackThumbs, visionSeeItOnMe1ItemEnabled, visionSeeItOnMeZoomEnabled));
  // splitAt returns undefined if splitIndex is out of bounds,
  // so we default to an empty array for the remaining product cards.
  const [firstRow, remainingProductCards] = A.splitAt(productCards, splitIndex) || [productCards, []];
  if (A.isNotEmpty(remainingProductCards) && isPromoCardShown) {
    return [F.toMutable(firstRow), insertElement(F.toMutable(remainingProductCards), <PromoCard key="promo-card" />, 4)];
  }
  return [F.toMutable(firstRow), F.toMutable(remainingProductCards)];
};
interface ResultsProps {
  shopProducts: ShopProductFragment[];
}

/**
 * This component handles rendering results grid and various other components
 */
const Results = ({
  shopProducts
}: ResultsProps) => {
  const {
    featureFlags
  } = useFeatureFlags('client', ['client.feedback_thumbs_enabled', 'client.web.vision_see_it_on_me_1_item', 'client.web.vision_see_it_on_me_zoom']);
  const showFeedbackThumbs = featureFlags?.['client.feedback_thumbs_enabled'];
  const {
    queryParams
  } = useSearchParams();
  const screenSize = useScreenSize();
  const splitIndex = screenSize === 'md' ? 3 : 4;
  const visionSeeItOnMe1ItemEnabled = featureFlags?.['client.web.vision_see_it_on_me_1_item'];
  const visionSeeItOnMeZoomEnabled = featureFlags?.['client.web.vision_see_it_on_me_zoom'];
  const [firstRow, remainingElements] = partitionElementsForPage({
    shopProducts,
    splitIndex,
    page: queryParams.page,
    showFeedbackThumbs,
    visionSeeItOnMe1ItemEnabled,
    visionSeeItOnMeZoomEnabled
  });
  return <VisionStatusProvider>
      <>
        {A.isNotEmpty(firstRow) && <Grid columns={{
        sm: 2,
        md: 3,
        lg: 4
      }} spacing={{
        sm: 'standard',
        lg: 'wide'
      }} mb={2}>
            {firstRow}
          </Grid>}

        <ResultsOutfits enableZoom={visionSeeItOnMeZoomEnabled} />

        {A.isNotEmpty(remainingElements) && <Grid columns={{
        sm: 2,
        md: 3,
        lg: 4
      }} spacing={{
        sm: 'standard',
        lg: 'wide'
      }}>
            {remainingElements}
          </Grid>}
      </>
    </VisionStatusProvider>;
};
export default Results;